import type { DecisionSummary, Grade, LiveAlert } from '../lib/api';

const LETTER: Record<Grade, string> = { Optimal: 'A', Acceptable: 'B', Questionable: 'C', Wrong: 'F' };
const GRADE_STYLE: Record<Grade, string> = {
  Optimal: 'bg-greenTint text-green', Acceptable: 'bg-blueTint text-blue', Questionable: 'bg-amberTint text-amber', Wrong: 'bg-redTint text-red',
};

const CATEGORY_LABEL: Record<string, string> = {
  fourth_down: 'Fourth down', two_point: 'Two point', field_goal_attempt: 'Field goal', timeout: 'Timeout',
  clock: 'Clock', challenge: 'Challenge', run_pass: 'Play call', red_zone_sequence: 'Red zone', coverage: 'Coverage',
  coverage_shell: 'Coverage shell', pass_rush: 'Pass rush', late_soft_coverage: 'Late coverage', kickoff_type: 'Kickoff',
  onside_omission: 'Onside kick', kick_return: 'Kick return', punt_return: 'Punt return', decision: 'Decision',
};

const humanize = (s: string | null | undefined) => (s ?? '').replace(/_/g, ' ');

export function CoachGradeCard({ d, team, game }: { d: DecisionSummary | LiveAlert; team?: string; game?: string }) {
  const cost = 'wp_cost_pct' in d ? d.wp_cost_pct : d.wp_impact_pct !== null && d.wp_impact_pct !== undefined ? -d.wp_impact_pct : null;
  const epa = 'epa_cost' in d ? d.epa_cost : null;
  return (
    <article className="card flex gap-3.5 p-3.5">
      <div className={`score flex h-14 w-14 shrink-0 items-center justify-center rounded-sm text-[34px] leading-none ${GRADE_STYLE[d.grade]}`}>{LETTER[d.grade]}</div>
      <div className="min-w-0 flex-1">
        <p className="meta flex flex-wrap gap-x-2 !text-[11px]">
          {team && <span className="font-bold text-ink">{team}</span>}
          <span>{CATEGORY_LABEL[d.category] ?? humanize(d.category)}</span>
          {d.quarter ? <span>Q{d.quarter}{'clock' in d && d.clock ? ` ${d.clock}` : ''}</span> : null}
          {game && <span>{game}</span>}
        </p>
        <p className="font-display text-[19px] font-bold capitalize leading-tight">{humanize(d.decision)}</p>
        {d.optimal && d.optimal !== d.decision && <p className="text-[13px] text-muted">Better call: <span className="font-semibold capitalize text-ink">{humanize(d.optimal)}</span></p>}
        {/* Both are costs and both print with a leading minus, but they arrive with
            opposite signs: wp_impact_pct is negative and flipped above, epa_cost is
            already positive. Taking the magnitude means neither can render as "--0.75"
            if a backend convention ever changes. */}
        {cost !== null && cost > 0 && <p className="num mt-1 text-[13px] text-red">-{Math.abs(cost).toFixed(1)}% win probability</p>}
        {(cost === null || cost === 0) && epa ? <p className="num mt-1 text-[13px] text-red">-{Math.abs(epa).toFixed(2)} expected points</p> : null}
        {d.description && <p className="mt-2 line-clamp-2 text-[13px] text-muted">{d.description}</p>}
      </div>
    </article>
  );
}
